import React, { useMemo } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { QrCode, Copy, ExternalLink, Printer, ShoppingBag } from "lucide-react";

export default function QRMenu() {
  const { currentCompany } = useAuth();
  const url = useMemo(() => currentCompany ? `${window.location.origin}/menu/${currentCompany.id}` : "", [currentCompany]);
  const kioskUrl = useMemo(() => currentCompany ? `${window.location.origin}/kiosk/${currentCompany.id}` : "", [currentCompany]);
  const qrSrc = url ? `https://api.qrserver.com/v1/create-qr-code/?size=400x400&data=${encodeURIComponent(url)}&margin=8` : "";

  const copy = async () => { try { await navigator.clipboard.writeText(url); toast.success("تم نسخ الرابط"); } catch { toast.error("فشل النسخ"); } };
  const copyKiosk = async () => { try { await navigator.clipboard.writeText(kioskUrl); toast.success("تم نسخ رابط شاشة العميل"); } catch { toast.error("فشل النسخ"); } };

  const print = () => {
    const w = window.open("", "_blank");
    if (!w) return;
    w.document.write(`
      <html dir="rtl"><head><title>قائمة ${currentCompany?.name || ""}</title>
      <style>body{font-family:sans-serif;text-align:center;padding:40px;margin:0;background:#fff}h1{color:#ea580c;margin-bottom:8px}h2{color:#64748b;font-weight:400;font-size:14px;margin-top:0}img{max-width:400px;width:100%;border:8px solid #fed7aa;border-radius:16px;margin:20px 0}p{color:#64748b;font-size:12px;max-width:400px;margin:8px auto}</style></head>
      <body><h1>🍽️ ${currentCompany?.name || ""}</h1><h2>امسح لعرض القائمة</h2><img src="${qrSrc}"/><p>${url}</p><p style="margin-top:30px;color:#94a3b8">مدعوم من محاسبتي</p></body></html>
    `);
    w.document.close(); setTimeout(() => w.print(), 500);
  };

  return (
    <div className="space-y-4" data-testid="qr-page">
      <div>
        <h1 className="font-heading font-bold text-2xl flex items-center gap-2"><QrCode className="w-6 h-6 text-primary" /> قائمة QR العامة</h1>
        <p className="text-sm text-muted-foreground mt-1">اطبع رمز الاستجابة السريعة وضعه على الطاولات — العملاء يمسحون لرؤية القائمة</p>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">رابط القائمة</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-2 gap-6 items-start">
            <div className="text-center space-y-3">
              {qrSrc && <img src={qrSrc} alt="QR" className="w-full max-w-[280px] mx-auto rounded-lg border-4 border-orange-100" data-testid="qr-img" />}
              <div className="flex gap-2 justify-center flex-wrap">
                <Button onClick={print} variant="outline" size="sm" data-testid="print-qr"><Printer className="w-4 h-4 me-1" /> طباعة</Button>
                <a href={url} target="_blank" rel="noopener noreferrer"><Button size="sm" variant="outline" data-testid="open-menu"><ExternalLink className="w-4 h-4 me-1" /> فتح</Button></a>
              </div>
            </div>
            <div className="space-y-3">
              <div>
                <div className="text-xs text-muted-foreground mb-1">رابط قائمتك:</div>
                <div className="flex gap-1">
                  <div className="flex-1 p-2 border rounded font-mono text-xs bg-muted/30 break-all">{url}</div>
                  <Button size="icon" variant="outline" onClick={copy} data-testid="copy-url"><Copy className="w-4 h-4" /></Button>
                </div>
              </div>
              <div className="p-3 rounded border bg-amber-50 text-xs space-y-2">
                <div className="font-bold text-amber-900">💡 نصائح:</div>
                <ul className="list-disc me-4 space-y-1 text-amber-800">
                  <li>القائمة تُحدَّث تلقائياً عند تعديل الأسعار أو إضافة أصناف</li>
                  <li>تأكد أن المنتجات لها صور واضحة وأوصاف مختصرة</li>
                  <li>يمكنك طباعة ملصق QR ووضعه على كل طاولة</li>
                  <li>الرابط يعمل حتى بدون إنترنت للعميل (Cache)</li>
                </ul>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-orange-200">
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><ShoppingBag className="w-4 h-4 text-orange-600"/> شاشة الطلب الذاتي للعميل</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">هذه صفحة منفصلة للعميل: تعرض الأصناف وتسمح له بإرسال طلب إلى المطبخ فقط، من دون تسجيل دخول أو أدوات إدارة. تبويب شاشة العميل يمنع الانتقال إلى صفحات الموظفين؛ أغلقه لمغادرة وضع الكشك. يُفضّل تشغيله على جهاز مخصص وبملء الشاشة، من دون جلسة موظف محفوظة عليه. الدفع يبقى عند الكاشير.</p>
          <div className="flex flex-col sm:flex-row gap-2">
            <a href={kioskUrl} target="_blank" rel="noopener noreferrer"><Button className="w-full sm:w-auto" data-testid="open-customer-kiosk"><ExternalLink className="w-4 h-4 me-1"/> فتح شاشة العميل</Button></a>
            <Button variant="outline" onClick={copyKiosk} data-testid="copy-customer-kiosk"><Copy className="w-4 h-4 me-1"/> نسخ رابط شاشة العميل</Button>
          </div>
          <div className="rounded border bg-muted/30 p-2 font-mono text-xs break-all" data-testid="customer-kiosk-url">{kioskUrl}</div>
        </CardContent>
      </Card>
    </div>
  );
}
